import React, { useMemo, useState, useEffect, useRef } from 'react';
import cloudinary from 'cloudinary-core';
import {
    getCloudinaryPathFromCloudinaryUrl,
    TCropType,
    TGravity,
    ICloudinaryTransformation,
    IVideoPlaybackProps,
    IMediaDimensions,
    IMediaPosition
} from './HBMediaElement';
import { isUrl } from '../../utils/shared.utils';
import styled from 'styled-components';

const CLOUD_NAME = 'honeybook';
const CLOUDINARY_VIDEO_BASE_URL =
    'https://res.cloudinary.com/honeybook/video/upload';

export interface IHBGifAsVideoProps
    extends IMediaDimensions,
        IMediaPosition,
        IVideoPlaybackProps {
    publicId: string;
    crop?: TCropType;
    gravity?: TGravity;
    transformations?: ICloudinaryTransformation[];
    className?: string;
    alt?: string;
    poster?: string;
    mediaRef?: React.Ref<HTMLVideoElement>;
    restartGif?: boolean;
}

const HBGifAsVideo: React.FC<IHBGifAsVideoProps> = ({
    publicId,
    width,
    height,
    crop,
    x,
    y,
    gravity,
    transformations = [],
    className,
    alt,
    autoPlay = true,
    loop = true,
    muted = true,
    playsInline = true,
    controls = false,
    poster,
    mediaRef,
    restartGif
}) => {
    const videoUrl = useMemo(() => {
        let path = publicId;

        if (isUrl(path)) {
            path = getCloudinaryPathFromCloudinaryUrl(path) || path;
        }

        // @ts-ignore
        const cloudinaryCore = new cloudinary.Cloudinary({
            cloud_name: CLOUD_NAME,
            secure: true
        });

        const baseTransformations: any[] = [
            {
                fetch_format: 'auto:video',
                quality: 'auto',
                flags: 'lossy'
            }
        ];

        if (crop) {
            baseTransformations.push({
                crop,
                width,
                height,
                x,
                y,
                gravity
            });
        } else if (width || height) {
            baseTransformations.push({
                width,
                height
            });
        }

        const allTransformations = [...baseTransformations, ...transformations];

        return cloudinaryCore.url(path, {
            resource_type: 'image',
            transformation: allTransformations
        });
    }, [publicId, crop, width, height, x, y, gravity, transformations]);

    const posterUrl = poster
        ? `${CLOUDINARY_VIDEO_BASE_URL}/${poster}`
        : undefined;

    const [restartCounter, setRestartCounter] = useState(0);
    const prevRestartGifRef = useRef(restartGif);

    useEffect(() => {
        if (restartGif && !prevRestartGifRef.current) {
            setRestartCounter(prev => prev + 1);
        }
        prevRestartGifRef.current = restartGif;
    }, [restartGif]);

    const videoKey =
        restartCounter > 0 ? `${videoUrl}-${restartCounter}` : videoUrl;

    return (
        <StyledVideo
            key={videoKey}
            ref={mediaRef}
            src={videoUrl}
            className={className}
            width={width}
            height={height}
            autoPlay={autoPlay}
            loop={loop}
            muted={muted}
            playsInline={playsInline}
            controls={controls}
            poster={posterUrl}
            aria-label={alt}
        >
            <track kind="captions" />
        </StyledVideo>
    );
};

export default HBGifAsVideo;

const StyledVideo = styled.video`
    width: 100%;
`;
